import { memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { StickyNote, Package, Ban, ListFilter } from "lucide-react";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type SortOption = 'id-desc' | 'id-asc' | 'total-price-desc' | 'nickname-asc';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'id-desc', label: '주문 순서 (최신 순)' },
  { value: 'id-asc', label: '주문 순서 (오래된 순)' },
  { value: 'total-price-desc', label: '총 구매액순' },
  { value: 'nickname-asc', label: '닉네임순' },
];

interface OrderDataTableProps {
  customers: CustomerTemplate[];
  selectedCustomerIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onViewDetails: (customerId: string) => void;
  onShowMemo: (customer: CustomerTemplate) => void;
  onKeepToggle: (customerId: string, currentKeepStatus: boolean) => void;
  sortOption: string;
  onSortChange: (value: string) => void;
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case "부분 입금":
      return "bg-amber-500";
    case "입금 완료":
      return "bg-yellow-500";
    case "출고 완료":
      return "bg-green-500";
    case "미지정":
      return "bg-gray-400";
    default:
      return "bg-yellow-500";
  }
};

function OrderDataTableImpl({ customers, selectedCustomerIds, onSelectionChange, onViewDetails, onShowMemo, onKeepToggle, sortOption, onSortChange }: OrderDataTableProps) {
  const handleSelectAll = (checked: boolean) => {
    onSelectionChange(checked ? customers.map(c => c.id) : []);
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelection = checked
      ? [...selectedCustomerIds, id]
      : selectedCustomerIds.filter(selectedId => selectedId !== id);
    onSelectionChange(newSelection);
  };

  const allSelected = selectedCustomerIds.length > 0 && selectedCustomerIds.length === customers.length;
 
   return (
     <TooltipProvider>
      <div>
        <div className="flex justify-end mb-2">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <ListFilter className="w-4 h-4 mr-2" />
                {sortOptions.find(o => o.value === sortOption)?.label || '정렬 기준'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onSortChange(option.value)}
                  disabled={sortOption === option.value}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="w-[50px] text-center">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="모든 항목 선택"
                  />
                </TableHead>
                <TableHead className="whitespace-nowrap">고객 닉네임</TableHead>
                <TableHead className="whitespace-nowrap">주문 시간</TableHead>
                <TableHead className="whitespace-nowrap">총 구매액</TableHead>
                <TableHead className="whitespace-nowrap">주문 상태</TableHead>
                <TableHead>상품 요약</TableHead>
                <TableHead className="whitespace-nowrap">킵</TableHead>
                <TableHead className="whitespace-nowrap">메모</TableHead>
                <TableHead className="whitespace-nowrap">작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer, index) => {
                const prevCustomer = customers[index - 1];
                const nextCustomer = customers[index + 1];

                const isFirstInGroup = !prevCustomer || prevCustomer.nickname !== customer.nickname;
                const isLastInGroup = !nextCustomer || nextCustomer.nickname !== customer.nickname;
                
                // A group of more than one item
                const isMultiItemGroup = !(isFirstInGroup && isLastInGroup);

                const rowClasses = cn({
                  // Add a distinct bottom border to the last row of ANY group.
                  "border-b-2 border-b-gray-300": isLastInGroup,
                  // For multi-item groups, remove the default bottom border for non-last items to make them look connected.
                  "border-b-0": isMultiItemGroup && !isLastInGroup,
                  // Add a subtle background to all items in a multi-item group
                  "bg-indigo-50/40": isMultiItemGroup,
                });

                return (
                  <TableRow key={customer.id} className={rowClasses}>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={selectedCustomerIds.includes(customer.id)}
                        onCheckedChange={(checked) => handleSelectRow(customer.id, !!checked)}
                        aria-label={`${customer.nickname} 선택`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{customer.nickname}</TableCell>
                    <TableCell>
                     {customer.createdAt ? (
                       <span className="text-xs text-gray-500">
                         {format(new Date(customer.createdAt), "M월 d일 HH:mm")}
                       </span>
                     ) : null}
                    </TableCell>
                    <TableCell className="font-semibold whitespace-nowrap">
                      {customer.totalPrice.toLocaleString()}원
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("text-white", getStatusVariant(customer.status))}>
                        {customer.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {customer.items && customer.items.length > 0 ? (
                        <span className="line-clamp-5 text-sm leading-snug">
                          {customer.items.map((item, i) => (
                            <span key={i}>
                              {i > 0 && <span className="text-gray-300">, </span>}
                              <span>{item.name}</span>
                              <span className="text-teal-500 text-xs ml-0.5">×{item.quantity}</span>
                            </span>
                          ))}
                        </span>
                      ) : "상품 없음"}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => onKeepToggle(customer.id, customer.is_keep)}>
                        {customer.is_keep ? <Package className="h-5 w-5 text-orange-500" /> : <Ban className="h-5 w-5 text-gray-300" />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {customer.memo && (
                        <Button variant="ghost" size="icon" onClick={() => onShowMemo(customer)}>
                          <StickyNote className="h-5 w-5 text-yellow-600" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => onViewDetails(customer.id)}>
                        상세보기
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </TooltipProvider>
  );
}

export const OrderDataTable = memo(OrderDataTableImpl);